/**
 * SINGLE SOURCE OF TRUTH for the managed-service retainers: Managed Marketing
 * and Complete Growth Partnership.
 *
 * These are NOT SaaS subscriptions and deliberately live outside tiers.ts.
 * They carry no feature entitlements, no billing product and no self-service
 * checkout: every engagement is scoped on a call and starts with a signed
 * service agreement, so every CTA routes to the contact page with the package
 * as query parameters. The pricing page, the structured data, llms.txt and the
 * knowledge-base PDF all read from here. Never restate a number elsewhere.
 *
 * Allowances (shorts, stories, page optimizations) are monthly TOTALS. A higher
 * package replaces the lower package's allowance; it never adds to it. One
 * unique edited video counts as one short however many platforms it is
 * published to, and Instagram stories are separate deliverables.
 */

export type ServiceId = 'marketing' | 'partnership';

export type ServiceTierId =
  | 'content-engine'
  | 'marketing-growth'
  | 'growth-partner'
  | 'revenue-engine'
  | 'enterprise-growth';

export type ServiceCategory = {
  id: ServiceId;
  name: string;
  /** One line on who this category is for. */
  summary: string;
  /** Whether the engagement includes (and so replaces the need for) a KIT website. */
  includesKitWebsite: boolean;
};

export type ServiceQuota = { label: string; value: string };

export type ServicePackage = {
  id: ServiceTierId;
  service: ServiceId;
  name: string;
  /** Monthly price in USD. For Enterprise Growth this is the starting price. */
  price: number;
  priceLabel: string;
  /** True when `price` is a floor ("$14,998+"), scoped per engagement. */
  priceIsFrom?: boolean;
  /** The one-line positioning shown under the name. */
  tagline: string;
  description: string;
  /** The headline allowances, shown as a short table on the card. */
  quotas: ServiceQuota[];
  included: string[];
  notIncluded: string[];
  customerResponsibilities: string[];
  /** The recommended package of its category. */
  featured?: boolean;
};

/** House style: no em dashes in anything a visitor reads. */
export const SERVICE_ENGAGEMENT_LABEL = 'Custom engagement. Scope confirmed on a call.';
export const SERVICE_CTA_LABEL = 'Discuss your growth plan';

/** What "a short" means, said once and reused everywhere it is counted. */
export const COUNTING_RULE =
  'One unique edited video counts as one short, even when it is published to several platforms. Instagram stories are separate deliverables, not extra shorts.';

/** The content workflow, in the order the work happens. */
export const CONTENT_FLOW = [
  'Research',
  'Ideas',
  'Scripts',
  'You record',
  'We edit',
  'Captions',
  'Scheduling',
  'Publishing',
  'Reporting',
];

export const SERVICE_CATEGORIES: ServiceCategory[] = [
  {
    id: 'marketing',
    name: 'Managed Marketing',
    summary: 'Keep your existing website and hire us for content and social.',
    includesKitWebsite: false,
  },
  {
    id: 'partnership',
    name: 'Complete Growth Partnership',
    summary: 'We run your website, software and marketing together.',
    includesKitWebsite: true,
  },
];

/** Things no managed-service package includes, whichever one you choose. */
const NEVER_INCLUDED = [
  'Paid advertising spend or ad management',
  'On-site filming',
  'Guaranteed rankings, leads, views or ROI',
  'Unlimited work',
];

const FOOTAGE = [
  'Providing usable raw footage',
  'Giving us access to your social accounts',
  'Timely content approvals',
];

const PARTNER_SOFTWARE = [
  'Scale-level KIT software included, with no separate subscription',
  'Managed website and hosting',
  'Field-locked CMS',
  'CRM and lead capture',
  'Visitor and lead analytics',
  'PDF-trained AI chatbot',
  'Booking engine',
  'Heatmaps and scroll depth',
  'A/B testing',
];

export const SERVICE_PACKAGES: ServicePackage[] = [
  {
    id: 'content-engine',
    service: 'marketing',
    name: 'Content Engine',
    price: 2498,
    priceLabel: '$2,498',
    tagline: 'Your outsourced content engine.',
    description:
      'We research what to say, write the scripts, edit your footage, then schedule and publish it for you.',
    quotas: [
      { label: 'Shorts', value: '15/mo' },
      { label: 'Instagram stories', value: '7/mo' },
      { label: 'Growth audit', value: 'Monthly' },
    ],
    included: [
      '15 researched, scripted, edited, scheduled and published shorts monthly',
      '7 Instagram story assets monthly',
      'Competitor and content research',
      'Hooks and scripting',
      'Editing client-provided footage',
      'Captions, scheduling and publishing',
      'Monthly growth audit and a review call',
    ],
    notIncluded: [
      'Local SEO (only on websites we host)',
      'Website development',
      'Paid advertising',
      'On-site filming',
      'Guaranteed views, leads or results',
    ],
    customerResponsibilities: FOOTAGE,
  },
  {
    id: 'marketing-growth',
    service: 'marketing',
    name: 'Marketing Growth',
    price: 3998,
    priceLabel: '$3,998',
    tagline: 'More content, more channels, sharper strategy.',
    description:
      'Everything in Content Engine at higher volume, published across more channels, with a monthly strategy session on what to make next.',
    quotas: [
      { label: 'Shorts', value: '20/mo in total' },
      { label: 'Instagram stories', value: '10/mo in total' },
      { label: 'Channels', value: 'Up to 5' },
      { label: 'Growth audit', value: 'Monthly' },
    ],
    included: [
      'Everything in Content Engine, with higher allowances',
      '20 researched, scripted, edited, scheduled and published shorts monthly in total',
      '10 Instagram story assets monthly in total',
      'Publishing to up to 5 channels: Instagram, TikTok, YouTube Shorts, Facebook and LinkedIn',
      'Audience targeting: who to talk to, not only what to say',
      'Monthly competitor intelligence',
      'Monthly growth audit of your content and enquiry funnel',
      'Monthly strategy call',
    ],
    notIncluded: [
      'Local SEO and website management (we only run SEO on websites we host, on software plans and growth partnerships)',
      'A replacement website',
      'Paid advertising management',
      'Unlimited content updates',
      'Guaranteed views, followers or leads',
    ],
    customerResponsibilities: FOOTAGE,
  },
  {
    id: 'growth-partner',
    service: 'partnership',
    name: 'Growth Partner',
    price: 4498,
    priceLabel: '$4,498',
    tagline: 'Your outsourced digital growth department.',
    description:
      'Your website, software, content and local search are run by one team. Built for single-location service businesses.',
    quotas: [
      { label: 'Shorts', value: '20/mo' },
      { label: 'Instagram stories', value: '10/mo' },
      { label: 'Page optimizations', value: 'Up to 2/mo' },
      { label: 'Local SEO', value: '1 location' },
    ],
    included: [
      '20 researched, scripted, edited, scheduled and published shorts monthly',
      '10 Instagram story assets monthly',
      ...PARTNER_SOFTWARE,
      'Local SEO for one location',
      'Google Business Profile management',
      'Local keyword research',
      'Up to 2 content or service-page optimizations monthly',
      'Competitor and content research',
      'Monthly competitor intelligence',
      'Conversion and lead-funnel review',
      'Monthly growth audit, reporting and strategy call',
    ],
    notIncluded: NEVER_INCLUDED,
    customerResponsibilities: FOOTAGE,
    featured: true,
  },
  {
    id: 'revenue-engine',
    service: 'partnership',
    name: 'Revenue Engine',
    price: 7998,
    priceLabel: '$7,998',
    tagline: 'We actively operate the digital revenue system.',
    description:
      'Everything in Growth Partner, plus active funnel operation and ongoing optimization. Not just more videos.',
    quotas: [
      { label: 'Shorts', value: '30/mo' },
      { label: 'Instagram stories', value: '15/mo' },
      { label: 'Page optimizations', value: 'Up to 4/mo' },
      { label: 'Local SEO', value: 'Up to 2 locations' },
    ],
    included: [
      'Everything in Growth Partner, with higher allowances',
      '30 researched, scripted, edited, scheduled and published shorts monthly in total',
      '15 Instagram story assets monthly in total',
      'Local SEO for up to 2 locations',
      'Up to 4 page optimizations monthly',
      'Weekly competitor and content research',
      'Weekly performance review',
      'Supported lead follow-up workflows',
      'Conversion and funnel optimization',
      'A/B testing program',
      'Landing-page iteration',
      'Review-generation campaigns',
      'Email nurture and content support',
      'Priority support',
      'Monthly growth audit and strategy meeting',
    ],
    notIncluded: NEVER_INCLUDED,
    customerResponsibilities: FOOTAGE,
  },
  {
    id: 'enterprise-growth',
    service: 'partnership',
    name: 'Enterprise Growth',
    price: 14998,
    priceLabel: '$14,998+',
    priceIsFrom: true,
    tagline: 'Coordinated digital growth operations across locations and markets.',
    description:
      'For 3 or more locations, multiple markets and franchise-style operations. Priced by scope, not by content volume.',
    quotas: [
      { label: 'Shorts', value: '30 to 40+/mo, by scope' },
      { label: 'Locations', value: '3 or more' },
      { label: 'Software', value: 'Included' },
    ],
    included: [
      '30 to 40+ researched, scripted, edited, scheduled and published shorts monthly, depending on scope',
      'Multiple locations',
      'Multi-location SEO',
      'Multiple Google Business Profiles',
      'Location-specific content strategy',
      'Location-specific landing pages',
      'Multi-market competitor intelligence',
      'Executive dashboards',
      'CRM and workflow integration',
      'Custom integrations, subject to feasibility',
      'Dedicated project coordination',
      'Executive reporting and a monthly growth audit per location',
      'Quarterly strategic planning',
      'Custom content volume',
    ],
    notIncluded: NEVER_INCLUDED,
    customerResponsibilities: FOOTAGE,
  },
];

/**
 * Commercial terms for every managed-service retainer. Software plans keep
 * their own terms (month to month, no minimum) and are not affected.
 */
export const RETAINER_TERMS = [
  'Billed monthly in advance, or yearly in advance at 8% off the monthly price.',
  'An initial 3-month commitment applies.',
  "After the first 3 months, services continue month to month with 30 days' written cancellation notice.",
  'Scope, onboarding, payment methods, exclusions and any additional expenses are confirmed in a signed service agreement.',
  'One managed marketing retainer per business and location. Retainers cannot be stacked to raise content allowances; larger volume means a higher package or a custom scope.',
  'Paid advertising spend, if any, is separate.',
  'No guaranteed rankings, leads, views or ROI.',
];

export function serviceCategory(id: ServiceId): ServiceCategory {
  return SERVICE_CATEGORIES.find((c) => c.id === id)!;
}

export function servicePackages(service: ServiceId): ServicePackage[] {
  return SERVICE_PACKAGES.filter((p) => p.service === service);
}

/** Consultation link for a package: the contact flow, never a checkout. */
export function serviceCtaHref(pkg: Pick<ServicePackage, 'service' | 'id'>): string {
  return `/contact?service=${pkg.service}&tier=${pkg.id}`;
}

export function getServicePackage(id: string | undefined | null): ServicePackage | undefined {
  return SERVICE_PACKAGES.find((p) => p.id === id);
}

export function servicePackage(id: ServiceTierId): ServicePackage {
  return SERVICE_PACKAGES.find((p) => p.id === id)!;
}
